import ApiClient from "@/services/api/apiClient";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import type { ApiResponse } from "@/services/types/common.types";
import type { Board, BoardClass, Subject } from "../types/curriculum.types";

/** Boards endpoint body: `{ boards: [...] }` (legacy `{ success, data: [...] }` still tolerated). */
type BoardsBody = Omit<ApiResponse<Board[]>, "success"> & {
  success?: boolean;
  boards?: Board[];
};

/**
 * GET api/v1/metadata/boards returns `{ boards: Board[] }`. It is normalised to the
 * `{ success, data: Board[] }` envelope so every `useBoards` consumer keeps reading `.data`.
 * With an `instituteId`, it is sent as the `institute_id` query param.
 */
export async function apiListBoards(params?: {
  instituteId?: string;
}): Promise<ApiResponse<Board[]>> {
  const instituteId = params?.instituteId?.trim();
  const raw = (await (instituteId
    ? ApiClient.get<Board[]>(API_ENDPOINTS.BOARDS.LIST, { institute_id: instituteId })
    : ApiClient.get<Board[]>(API_ENDPOINTS.BOARDS.LIST))) as unknown;
  if (Array.isArray(raw)) return { success: true, data: raw as Board[] };

  const { boards, data, success, ...rest } = (raw ?? {}) as BoardsBody;
  return {
    ...rest,
    success: success ?? true,
    data: Array.isArray(boards) ? boards : Array.isArray(data) ? data : [],
  };
}

/**
 * GET api/v1/helpers/get-board-classes?board_id={id} returns `{ classes: [{ id, name }] }`.
 * Normalised to the `{ success, data: BoardClass[] }` envelope (`board_id` is filled in
 * from the request).
 */
export async function apiListBoardClassLookup(
  boardId: number | string,
): Promise<ApiResponse<BoardClass[]>> {
  const raw = (await ApiClient.get(API_ENDPOINTS.HELPERS.BOARD_CLASSES, {
    board_id: boardId,
  })) as unknown as {
    success?: boolean;
    message?: string;
    classes?: { id: number; name: string }[];
  } | null;

  if (raw?.success === false) {
    throw new Error(raw.message || "Failed to load classes");
  }
  const classes = Array.isArray(raw?.classes) ? raw.classes : [];
  return {
    success: true,
    data: classes.map((c) => ({
      id: c.id,
      name: c.name,
      board_id: Number(boardId),
    })),
  };
}

/**
 * GET api/v1/helpers/get-subjects?board_id={id}&class_id={id} returns
 * `{ subjects: [{ id, name, slug, is_optional }] }`. Normalised to the
 * `{ success, data: Subject[] }` envelope (`board_id` / `class_id` filled from the request).
 */
export async function apiListSubjectLookup(
  boardId: number | string,
  classId: number | string,
): Promise<ApiResponse<Subject[]>> {
  const raw = (await ApiClient.get(API_ENDPOINTS.HELPERS.SUBJECTS, {
    board_id: boardId,
    class_id: classId,
  })) as unknown as {
    success?: boolean;
    message?: string;
    subjects?: { id: number; name: string; slug?: string }[];
  } | null;

  if (raw?.success === false) {
    throw new Error(raw.message || "Failed to load subjects");
  }
  const subjects = Array.isArray(raw?.subjects) ? raw.subjects : [];
  return {
    success: true,
    data: subjects.map((s) => ({
      id: s.id,
      name: s.name,
      slug: s.slug,
      board_id: Number(boardId),
      class_id: Number(classId),
    })),
  };
}

export async function apiListBoardClasses(
  boardId: number | string,
): Promise<ApiResponse<BoardClass[]>> {
  return (await ApiClient.get<BoardClass[]>(
    API_ENDPOINTS.BOARDS.CLASSES(boardId),
  )) as ApiResponse<BoardClass[]>;
}

export async function apiListBoardClassSubjects(
  boardId: number | string,
  classId: number | string,
): Promise<ApiResponse<Subject[]>> {
  return (await ApiClient.get<Subject[]>(
    API_ENDPOINTS.BOARDS.SUBJECTS(boardId, classId),
  )) as ApiResponse<Subject[]>;
}
